import React from "react";
import { SupportTicket } from "@core/services/SupportTicketService";
import { AIAnalysisResult } from "@core/services/SupportAIService";
import { SupportAIService } from "@core/services/SupportAIService";
import { CustomerPlanBadge } from "./CustomerPlanBadge";
import {
  AlertCircle,
  MessageSquare,
  Clock,
  ShieldAlert,
  User,
  ExternalLink,
  ChevronRight,
  AlertTriangle,
  Brain,
} from "lucide-react";

interface TicketSummaryCardProps {
  ticket: SupportTicket;
  onClick: (id: string) => void;
}

export const TicketSummaryCard: React.FC<TicketSummaryCardProps> = ({
  ticket,
  onClick,
}) => {
  const aiInfo = ticket.metadata?.ai_analysis as AIAnalysisResult | undefined;

  // Calcul SLA
  const calculateSLA = () => {
    const slaDays = SupportAIService.getSLADays(ticket.severity as any);
    const createdDate = new Date(ticket.created_at);
    const dueDate = new Date(
      createdDate.getTime() + slaDays * 24 * 60 * 60 * 1000,
    );
    const now = new Date();
    const overdueDays = Math.floor(
      (now.getTime() - dueDate.getTime()) / (24 * 60 * 60 * 1000),
    );

    return {
      isOverdue: overdueDays >= 0,
      overdueDays,
      dueDate,
      slaDays,
    };
  };

  const slaInfo = calculateSLA();

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case "Critical":
        return "text-red-400 border-red-500/50 bg-red-500/10";
      case "High":
        return "text-orange-400 border-orange-500/50 bg-orange-500/10";
      case "Normal":
        return "text-blue-400 border-blue-500/50 bg-blue-500/10";
      default:
        return "text-gray-400 border-gray-500/50 bg-gray-500/10";
    }
  };

  const getPriorityColor = (priority?: number | null) => {
    switch (priority) {
      case 3:
        return "text-red-400 border-red-500/50 bg-red-500/10";
      case 2:
        return "text-orange-400 border-orange-500/50 bg-orange-500/10";
      case 1:
        return "text-yellow-400 border-yellow-500/50 bg-yellow-500/10";
      case 0:
        return "text-green-400 border-green-500/50 bg-green-500/10";
      default:
        return "text-gray-400 border-gray-500/50 bg-gray-500/10";
    }
  };

  const getPriorityLabel = (priority?: number | null) => {
    switch (priority) {
      case 3:
        return "Critique";
      case 2:
        return "Haute";
      case 1:
        return "Normale";
      case 0:
        return "Basse";
      default:
        return "Non définie";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "resolved":
        return <div className="w-2 h-2 rounded-full bg-green-500" />;
      case "in_progress":
        return (
          <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
        );
      default:
        return <div className="w-2 h-2 rounded-full bg-blue-500" />;
    }
  };

  return (
    <div
      className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:bg-gray-800/70 hover:border-purple-500/30 transition-all duration-300 cursor-pointer group"
      onClick={() => onClick(ticket.id)}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {getStatusIcon(ticket.status)}
          <span className="text-xs font-medium uppercase tracking-wider text-gray-400">
            {ticket.status.replace("_", " ")}
          </span>
          {/* Priorité - toujours visible */}
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-bold ${getPriorityColor(ticket.priority)}`}
          >
            <AlertCircle className="w-3 h-3" />P{ticket.priority ?? "?"} -{" "}
            {getPriorityLabel(ticket.priority)}
          </div>
          {/* Badge client — indique si le ticket vient d'un client payant */}
          <CustomerPlanBadge
            planType={ticket.customer?.planType}
            subscriptionStatus={ticket.customer?.subscriptionStatus}
            compact
          />
          {slaInfo.isOverdue && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 border border-red-500/30">
              <AlertTriangle className="w-3 h-3 text-red-400" />
              <span className="text-xs text-red-400 font-medium">
                {slaInfo.overdueDays}j retard
              </span>
            </div>
          )}
        </div>
        <span className="text-[10px] text-gray-500 font-mono">
          #{ticket.id.slice(0, 8)}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-white mb-2 line-clamp-1 group-hover:text-purple-400 transition-colors">
        {ticket.subject}
      </h3>

      <div className="flex items-center justify-between text-sm mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
              ticket.customer?.planType && ticket.customer.planType !== "free"
                ? "bg-gradient-to-br from-purple-500 to-amber-400 ring-2 ring-purple-400/40"
                : "bg-gradient-to-br from-gray-500 to-gray-600"
            }`}
          >
            {ticket.email.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-white font-medium">
              {ticket.email.split("@")[0]}
            </p>
            <p className="text-gray-400 text-xs">
              {ticket.email.split("@")[1]}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-gray-400">
          <Clock className="w-4 h-4" />
          <span className="text-xs">
            {new Date(ticket.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {aiInfo && (
        <div className="flex flex-wrap gap-2 mb-4">
          <div
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold backdrop-blur-sm ${getSeverityColor(aiInfo.severity)} shadow-sm`}
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            {aiInfo.severity}
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-purple-500/30 bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-300 text-xs font-semibold backdrop-blur-sm shadow-sm">
            <Brain className="w-3.5 h-3.5" />
            {aiInfo.classification}
          </div>
          {aiInfo.sentiment && (
            <div className="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-gray-300 text-xs font-medium backdrop-blur-sm">
              {aiInfo.sentiment}
            </div>
          )}
        </div>
      )}

      {aiInfo?.summary && (
        <div className="bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-lg p-3 border border-purple-500/10 mb-4">
          <p className="text-sm text-gray-300 line-clamp-2 italic">
            "{aiInfo.summary}"
          </p>
        </div>
      )}

      <div className="flex justify-between items-center pt-4 border-t border-white/10 group-hover:border-white/20 transition-all duration-300">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <MessageSquare className="w-3 h-3" />
          {ticket.metadata?.message_count || 1} messages
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick(ticket.id);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-semibold rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-purple-500/25"
        >
          Voir les détails <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
